import {
  Button,
  ListItemIcon,
  ListItemText,
  MenuList,
  Stack,
  Typography,
  TypographyProps,
} from "@mui/material";
import { useMemo, useState } from "react";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AuthorizedTenantElement from "@/common/components/Auth/AuthorizedTenantElement";
import AppDropdownButton from "@/common/components/Button/AppDropdownButton";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppLink from "@/common/components/Link/AppLink";
import AppMenuItem from "@/common/components/Menu/AppMenuItem";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { TypeHelper } from "@/common/helpers/type";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DamageDetectionDto,
  DataUpdatesHubClientMethodName,
  EntityType,
  VehicleOperationType,
} from "@/core/api/generated/v1";

import { VehicleOperationViewPageHeader } from "../../Vehicle/View/Header/VehicleOperationViewPageHeader";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import { DamageDetectionCreateLocalDamageCostEvaluationModal } from "../DamageDetectionCreateLocalDamageCostEvaluationModal";
import DamageDetectionMenu from "../DamageDetectionMenu";
import DamageDetectionSendToExternalDamageCostEvaluationModal from "../DamageDetectionSendToExternalDamageCostEvaluationModal";
import CommentsTabContent from "./Tabs/CommentsTabContent";
import ContractTabContent from "./Tabs/ContractTabContent";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import InboxTabContent from "./Tabs/InboxTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";
import RelatedOperationsTabContent from "./Tabs/RelatedOperationsTabContent";
import VehicleTabContent from "./Tabs/VehicleTabContent";

export enum DamageDetectionViewPageTabs {
  Overview = "Overview",
  Inbox = "Inbox",
  Vehicle = "Vehicle",
  Contract = "Contract",
  RelatedOperations = "RelatedOperations",
  GeneralHistory = "GeneralHistory",
  Comments = "Comments",
}

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};
const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

export interface DamageDetectionViewProps extends BaseEntityViewInheritableProps<DamageDetectionDto> {
  damageDetectionId: string | null | undefined;
  damageDetection: DamageDetectionDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  headerProps?: Partial<typeof defaultHeaderProps>;
}

export default function DamageDetectionView({
  damageDetectionId,
  damageDetection,
  displayProps,
  headerProps,
  onUpdate,
  onDelete,
  onClose,
}: DamageDetectionViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };
  headerProps = {
    ...defaultHeaderProps,
    ...headerProps,
  };

  const history = useAppHistory();
  const currentTenant = useCurrentTenant();
  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityView(EntityType.DamageDetection),
      viewVariant: displayProps?.viewVariant,
    },
  });

  const [isStartLocalDamageCostEvaluationModalOpen, setIsStartLocalDamageCostEvaluationModalOpen] =
    useState(false);
  const [
    isSendToExternalDamageCostEvaluationModalOpen,
    setIsSendToExternalDamageCostEvaluationModalOpen,
  ] = useState(false);

  const damageDetectionRequest = useApiRequest(
    apiClient.damageDetectionsApi.apiV1DamageDetectionsDamageDetectionIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      damageDetectionId: damageDetectionId!,
    },
    {
      deps: [damageDetectionId],
      skip: !damageDetectionId,
    },
  );
  damageDetection = damageDetection || damageDetectionRequest?.data;

  const vehicleDamageIds = useMemo(
    () => damageDetection?.items?.map((x) => x.damage?.id || "").filter(Boolean),
    [damageDetection],
  );
  const isAnyItem = TypeHelper.isNotEmptyArray(damageDetection?.items);
  const isAnyVehicleDamage = TypeHelper.isNotEmptyArray(vehicleDamageIds);

  // const visualModelRequest = useApiRequest(
  //   apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsModelIdGet,
  //   {
  //     modelId: damageDetection?.vehicleVisualModel?.id || "",
  //   },
  //   {
  //     deps: [damageDetection?.vehicleVisualModel?.id],
  //     skip: !damageDetection?.vehicleVisualModel?.id,
  //   },
  // );
  // const visualModel = visualModelRequest?.data;

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(
        currentTenant?.id,
        EntityType.DamageDetection,
        damageDetectionId || "",
      ),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      damageDetectionRequest.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb: damageDetection && {
      idValue: damageDetection?.id || "",
      newTitle: damageDetection?.localNumber || "",
    },
  });

  return (
    <BaseEntityView
      entityType={EntityType.DamageDetection}
      entityId={damageDetectionId}
      entity={damageDetection}
      entityRequest={damageDetectionRequest}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          damageDetection && (
            <VehicleOperationViewPageHeader
              operationType={VehicleOperationType.DamageDetection}
              operationEntity={damageDetection}
              vehicleId={damageDetection?.vehicle?.id}
              vehicle={damageDetection?.vehicle || undefined}
              titleProps={{
                to: headerProps?.withLink
                  ? ROUTE_PATH.DAMAGE_DETECTION_VIEW(damageDetection?.id)
                  : undefined,
                typographyProps: headerProps?.typographyProps,
              }}
              primaryActions={
                displayProps.actions && (
                  <Stack direction='row' gap={1} flexWrap='wrap'>
                    {damageDetection?.contract && (
                      <Button
                        variant='outlined'
                        color='text'
                        sx={{ ml: { md: "auto" } }}
                        component={AppLink}
                        to={ROUTE_PATH.CONTRACT_VIEW(damageDetection?.contract.id)}
                        startIcon={<AppIcon of='documents' />}
                      >
                        <Typography mr={1} ml={1}>
                          Contract
                        </Typography>
                        <Typography>
                          {damageDetection?.contract?.externalNumber ||
                            damageDetection?.contract?.localNumber}
                        </Typography>
                      </Button>
                    )}

                    {(isAnyItem || isAnyVehicleDamage) && (
                      <AuthorizedTenantElement tenantIds={[damageDetection?.tenantId]}>
                        <AuthorizedElement
                          permissionsAny={[
                            AppPermission.DamageCostEvaluationPerform,
                            AppPermission.DamageCostEvaluationManage,
                            AppPermission.DamageDetectionManage,
                            AppPermission.TenantRequestManage,
                          ]}
                        >
                          <AppDropdownButton
                            variant='outlined'
                            size='medium'
                            color='text'
                            autoCloseOnClick
                            buttonProps={{
                              startIcon: <AppIcon of='send' />,
                            }}
                            dropdownContentWrapperProps={{
                              minWidth: 200,
                              maxHeight: {
                                xxs: "90vh",
                                md: 300,
                              },
                            }}
                            dropdownContent={() => (
                              <MenuList>
                                {isAnyItem && (
                                  <AuthorizedElement
                                    permissions={[AppPermission.DamageDetectionManage]}
                                    permissionsAny={[
                                      AppPermission.DamageCostEvaluationPerform,
                                      AppPermission.DamageCostEvaluationManage,
                                    ]}
                                  >
                                    <AppMenuItem
                                      onClick={() => {
                                        setIsStartLocalDamageCostEvaluationModalOpen(true);
                                      }}
                                    >
                                      <ListItemIcon>
                                        <AppIcon of='damageCostEvaluation' />
                                      </ListItemIcon>
                                      <ListItemText>Create local cost evaluation</ListItemText>
                                    </AppMenuItem>
                                  </AuthorizedElement>
                                )}

                                {isAnyVehicleDamage && (
                                  <AuthorizedElement
                                    permissions={[
                                      AppPermission.DamageDetectionManage,
                                      AppPermission.TenantRequestManage,
                                    ]}
                                  >
                                    <AppMenuItem
                                      onClick={() => {
                                        setIsSendToExternalDamageCostEvaluationModalOpen(true);
                                      }}
                                    >
                                      <ListItemIcon>
                                        <AppIcon of='tenantRequest' />
                                      </ListItemIcon>
                                      <ListItemText>Send to external cost evaluation</ListItemText>
                                    </AppMenuItem>
                                  </AuthorizedElement>
                                )}
                              </MenuList>
                            )}
                          >
                            Send to assessment
                          </AppDropdownButton>
                        </AuthorizedElement>
                      </AuthorizedTenantElement>
                    )}

                    <AuthorizedTenantElement tenantIds={[damageDetection?.tenantId]}>
                      <AuthorizedElement permissions={[AppPermission.DamageDetectionManage]}>
                        <Button
                          sx={{ minHeight: 0 }}
                          component={AppLink}
                          to={ROUTE_PATH.DAMAGE_DETECTION_EDIT(damageDetection?.id)}
                          variant='outlined'
                          color='text'
                          size='medium'
                          startIcon={<AppIcon of='edit' />}
                        >
                          Edit
                        </Button>
                      </AuthorizedElement>
                    </AuthorizedTenantElement>
                  </Stack>
                )
              }
              secondaryActions={
                <DamageDetectionMenu
                  entity={damageDetection}
                  displayProps={{
                    actions: {
                      edit: false,
                      delete: true,
                      addToContract: true,
                      addToAssignment: true,
                      downloadPdf: true,
                      migrateToNewVisualModel: true,
                      viewAllDamages: true,
                      createDamageCostEvaluation: true,
                      createRepairOperation: true,
                      sendToExternalDamageCostEvaluation: true,
                    },
                  }}
                  onUpdate={(newValue) => {
                    newValue
                      ? damageDetectionRequest.replaceData(newValue)
                      : damageDetectionRequest.refetch();
                    onUpdate?.(newValue);
                  }}
                  onDelete={() => (onDelete ? onDelete() : history.goBack())}
                />
              }
              closeAction={
                displayProps.closeInHeader && onClose ? (
                  <AppModalCloseIconButton onClick={onClose} />
                ) : undefined
              }
              onTagsUpdated={(newValue) => {
                damageDetectionRequest.updateData((x) => {
                  x.tags = newValue || undefined;
                  onUpdate?.(x);
                });
              }}
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={DamageDetectionViewPageTabs}
          defaultTabId={DamageDetectionViewPageTabs.Overview}
          commonViewParams={commonViewParams}
          tabs={[
            { label: "Overview", value: DamageDetectionViewPageTabs.Overview, isHideable: false },
            { label: "Chats", value: DamageDetectionViewPageTabs.Inbox },
            {
              label: "Vehicle",
              value: DamageDetectionViewPageTabs.Vehicle,
              permissions: [AppPermission.VehicleRead],
            },
            ...(damageDetection?.contract?.id
              ? [
                  {
                    label: "Contract",
                    value: DamageDetectionViewPageTabs.Contract,
                    permissions: [AppPermission.ContractRead],
                  },
                ]
              : []),
            { label: "Related operations", value: DamageDetectionViewPageTabs.RelatedOperations },
            {
              label: "History",
              value: DamageDetectionViewPageTabs.GeneralHistory,
              permissions: [AppPermission.GeneralHistoryRead],
            },
            { label: "Comments", value: DamageDetectionViewPageTabs.Comments },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            damageDetection && (
              <>
                {activeTab === DamageDetectionViewPageTabs.Overview && (
                  <OverviewTabContent
                    onUpdate={(newValue) => {
                      newValue
                        ? damageDetectionRequest.replaceData(newValue)
                        : damageDetectionRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                    damageDetection={damageDetection}
                  />
                )}
                {activeTab === DamageDetectionViewPageTabs.Inbox && (
                  <InboxTabContent damageDetection={damageDetection} />
                )}
                {activeTab === DamageDetectionViewPageTabs.Vehicle && (
                  <VehicleTabContent damageDetection={damageDetection} />
                )}
                {activeTab === DamageDetectionViewPageTabs.Contract && (
                  <ContractTabContent damageDetection={damageDetection} />
                )}
                {activeTab === DamageDetectionViewPageTabs.RelatedOperations && (
                  <RelatedOperationsTabContent damageDetection={damageDetection} />
                )}
                {activeTab === DamageDetectionViewPageTabs.GeneralHistory && (
                  <GeneralHistoryTabContent damageDetection={damageDetection} />
                )}
                {activeTab === DamageDetectionViewPageTabs.Comments && (
                  <CommentsTabContent damageDetection={damageDetection} />
                )}
              </>
            )
          }
        </PageTabsV2>
      </ViewLayout>

      {/* Create DCE modal */}
      {damageDetection && (
        <DamageDetectionCreateLocalDamageCostEvaluationModal
          open={isStartLocalDamageCostEvaluationModalOpen}
          onClose={() => setIsStartLocalDamageCostEvaluationModalOpen(false)}
          damageDetection={damageDetection}
          onSave={(newValue) => {
            history.push(ROUTE_PATH.DAMAGE_COST_EVALUATION_VIEW(newValue.id));
          }}
        />
      )}

      {/* Send to external DCE modal */}
      {damageDetection && isSendToExternalDamageCostEvaluationModalOpen && (
        <DamageDetectionSendToExternalDamageCostEvaluationModal
          open={isSendToExternalDamageCostEvaluationModalOpen}
          onClose={() => setIsSendToExternalDamageCostEvaluationModalOpen(false)}
          damageDetection={damageDetection}
          onSave={(newValue) => {
            damageDetectionRequest.replaceData(newValue.damageDetection);
          }}
        />
      )}
    </BaseEntityView>
  );
}
